import React, { useState, useEffect, useCallback } from "react";
import { ReadyState, SendMessage } from "react-use-websocket";
import { Button } from "../Button";

type Props = {
    path: string,
    lastMessage: MessageEvent<any> | null,
    sendMessage: SendMessage,
    readyState: ReadyState,
};

const getInitialState = () => {
  const cachedCustomBehaviorCommands = sessionStorage.getItem("customBehaviorCommands");
  return cachedCustomBehaviorCommands ? JSON.parse(cachedCustomBehaviorCommands) : []
}

const MyNewComponent: React.FC<Props> = ({
    path, lastMessage, sendMessage, readyState,
}) => {

    const [customBehaviorCommands, setCustomBehaviorCommands] = useState<string[]>(getInitialState);
    const [newBehaviorCommand, setNewBehaviorCommand] = useState<string>("");
    const [heldDisplayCmd, setHeldDisplayCmd] = useState<string>("");
    const [taughtActions, setTaughtActions] = useState<string[]>([]);
    const [armedForget, setArmedForget] = useState<string>("");
    const [taughtPath, setTaughtPath] = useState<string>("");

    // the display switches queue up, so pressing the same one twice leaves a second switch
    // waiting behind the first and it takes effect the moment you switch back -- looks like the
    // display is stuck. hold that button for a few seconds after a press. the other one stays
    // live so you can always switch the other way immediately.
    useEffect(() => {
        if (heldDisplayCmd === "") return;
        const timer = setTimeout(() => setHeldDisplayCmd(""), 10000);
        return () => clearTimeout(timer);
    }, [heldDisplayCmd]);

    useEffect(() => {
        if(lastMessage === null) return;
        const data = JSON.parse(lastMessage.data);
        if (Array.isArray(data.paths)) {
            const taught = data.paths.find((p: string) => p.startsWith("avaTaughtActions"));
            setTaughtPath(taught ?? "");
            if (taught) sendMessage(JSON.stringify({ "path": taught, "method": "startup" }));
            return;
        }
        if (!data.path) return;
        if (data.path === taughtPath && data.taughtActions) {
            setTaughtActions(data.taughtActions);
        }
    }, [path, lastMessage, taughtPath, sendMessage]);

    // forgetting is permanent, so the first press only arms the button
    useEffect(() => {
        if (armedForget === "") return;
        const timer = setTimeout(() => setArmedForget(""), 5000);
        return () => clearTimeout(timer);
    }, [armedForget]);

    const handleForget = useCallback((name: string) => {
        if (armedForget !== name) {
            setArmedForget(name);
            return;
        }
        setArmedForget("");
        sendMessage(JSON.stringify({
            "path": taughtPath,
            "method": "forgetTaughtAction",
            "name": name,
            "actor": "ava"
        }));
    }, [sendMessage, taughtPath, armedForget]);

    const handleRemoveCustomBehavior = useCallback((command: string) => {
      const newCommands = customBehaviorCommands.filter(c => c !== command)
      setCustomBehaviorCommands(newCommands)
      sessionStorage.setItem("customBehaviorCommands", JSON.stringify(newCommands))
    }, [customBehaviorCommands]);

    const handleSubmit = useCallback((command: string) => {
        sendMessage(JSON.stringify({
            "path": path,
            "input": command,
            "speaker": "webapp",
            "addressee": "ava"
        }));
    }, [sendMessage, path]);

    const generateCustomBehaviors = useCallback(() => {
        return customBehaviorCommands.map((value, index) => (
            <div key={index} className="relative group rounded-md bg-muted overflow-hidden">
                <button
                    onClick={() => handleSubmit(value)}
                    className="w-full px-3 py-2.5 pr-7 text-xs font-medium hover:bg-muted/70 active:bg-muted/50 transition-colors text-left text-foreground break-words"
                >
                    {value}
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveCustomBehavior(value); }}
                    className="absolute top-1 right-1 h-5 w-5 flex items-center justify-center rounded
                               text-muted-foreground hover:text-destructive hover:bg-destructive/20
                               transition-colors text-sm font-bold"
                    title="Remove"
                >
                    {"\u00D7"}
                </button>
            </div>
        ));
    }, [customBehaviorCommands, handleSubmit, handleRemoveCustomBehavior]);

    const handleAddCustomBehavior = () => {
      if (newBehaviorCommand.length === 0) return;
        const newCommands = [
                                ...customBehaviorCommands,
                                newBehaviorCommand
                            ]
      setCustomBehaviorCommands(newCommands)
      sessionStorage.setItem("customBehaviorCommands", JSON.stringify(newCommands))
      setNewBehaviorCommand("");
    };

    return (
        <div className="flex flex-col w-full min-h-0 flex-1 gap-4 overflow-auto">
            <div className="w-full space-y-4">
                <div className="flex gap-3">
                    <div className="flex-1 border border-border rounded-md bg-card overflow-hidden">
                        <div className="px-3 py-2 border-b border-border bg-muted/30">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Quick Actions
                            </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2">
                            {[
                                { label: "Go to Dock", cmd: "go home" },
                                { label: "Set display to TRACS", cmd: "switch display to TRACS" },
                                { label: "Set display to Telepresence", cmd: "switch display to Telepresence" },
                                { label: "Lower media volume", cmd: "lower media volume" },
                                { label: "Raise media volume", cmd: "raise media volume" },
                            ].map(({ label, cmd }) => (
                                <button
                                    key={cmd}
                                    disabled={heldDisplayCmd === cmd}
                                    onClick={() => {
                                        if (cmd.startsWith("switch display")) setHeldDisplayCmd(cmd);
                                        handleSubmit(cmd);
                                    }}
                                    className="px-3 py-2.5 text-xs font-medium rounded-md bg-muted hover:bg-muted/70 active:bg-muted/50 transition-colors text-left text-foreground disabled:opacity-40 disabled:pointer-events-none"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={() => { setHeldDisplayCmd(""); handleSubmit("stop"); }}
                        className="shrink-0 self-stretch px-4 rounded-md border border-destructive/40 
                                   text-xs font-medium text-destructive bg-destructive/5 
                                   hover:bg-destructive hover:text-white hover:border-destructive
                                   transition-colors"
                    >
                        Cancel
                    </button>
                </div>

                {taughtPath !== "" && <div className="border border-border rounded-md bg-card overflow-hidden">
                    <div className="px-3 py-2 border-b border-border bg-muted/30 flex justify-between items-center">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Taught Actions
                        </span>
                        <button
                            onClick={() => sendMessage(JSON.stringify({ "path": taughtPath, "method": "taughtActions" }))}
                            className="text-xs text-muted-foreground hover:text-foreground"
                            title="Refresh"
                        >
                            Refresh
                        </button>
                    </div>
                    {taughtActions.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            No taught actions
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2 p-3">
                            {taughtActions.map((name) => (
                                <div key={name} className="flex items-center gap-1 rounded-md bg-muted overflow-hidden">
                                    <span className="px-3 py-2.5 text-xs font-medium text-foreground break-words">
                                        {name}
                                    </span>
                                    <button
                                        onClick={() => handleForget(name)}
                                        className={"px-2 py-2.5 text-xs font-medium transition-colors "
                                            + (armedForget === name
                                                ? "bg-destructive text-white"
                                                : "text-muted-foreground hover:text-destructive hover:bg-destructive/20")}
                                        title="Forget this taught action"
                                    >
                                        {armedForget === name ? "Confirm" : "Forget"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>}

                <div className="border border-border rounded-md bg-card overflow-hidden">
                    <div className="px-3 py-2 border-b border-border bg-muted/30">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Custom Commands
                        </span>
                    </div>
                    {customBehaviorCommands.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            No custom commands yet
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2 p-3">
                            {generateCustomBehaviors()}
                        </div>
                    )}
                    <div className="flex gap-2 p-3 border-t border-border bg-muted/30">
                        <input
                            type="text"
                            className="flex-1 h-8 rounded-md text-sm border border-input bg-background 
                                       text-foreground px-3 placeholder:text-muted-foreground
                                       focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                            placeholder="Add custom command..."
                            value={newBehaviorCommand}
                            onChange={(e) => setNewBehaviorCommand(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddCustomBehavior()}
                        />
                        <Button size="sm" onClick={() => handleAddCustomBehavior()}>
                            Add
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyNewComponent;
